import ReactDOM from "react-dom";
import ProfilePreview from "../../../profile/presentation/components/ProfilePreview";
import { Likes } from "../../../match/domain/entities/userMatch";
import { TextComponent } from "../../../../core/components/TextComponent";
import { Column } from "../../../../core/components/Column";
import { Row } from "../../../../core/components/Row";
import { CustomButton } from "../../../../core/components/CustomButton";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { deleteMyNotificationAction } from "../../../match/presentation/store/MatchSlice";
import useMatch from "../../../match/presentation/hook/useMatch";
import { useHandleDeleteNotification } from "../hook/useHandleDeleteNotification";
import { ConfirmationModal } from "./ConfirmationModal";
import { setSelectedChat } from "../../../chat/presentation/store/ChatSlice";
import { useNavigate } from "react-router-dom";
import { Chats } from "../../../chat/domain/entities/getChat";
import { ButtonLikeDislike } from "../../../match/presentation/components/ButtonLikeDisLike";
import { PurchaseMessage } from "../../../../core/components/PurchaseMessage";
import { formatPublicProfileDistanceText } from "../../../../core/utilities/distance";
import { UserView } from "../../../../core/components/UserView";
import { CheckVerify } from "../../../../core/icons/CheckVerify";
import { sortPhotos } from "../../../../core/constants/constants";
import { ArrowDown2 } from "../../../../core/icons/ArrowDown";
import { InstantMessageReceipt } from "./InstantMessageReceipt";

export const ShowUserInfoProfile = ({
	userInfoProfile,
	handleProfilePreview,
	setUserInfoProfile,
	handleNotification,
	activeCategory,
	desktopFullPage = false,
}: {
	userInfoProfile: Partial<Likes | undefined>;
	handleProfilePreview: () => void;
	handleNotification: () => void;
	activeCategory: "like" | "match" | "alert" | "myLikes";
	setUserInfoProfile: React.Dispatch<React.SetStateAction<Partial<Likes | undefined>>>;
	desktopFullPage?: boolean;
}) => {
	const { user } = useSelector((state: RootState) => state.onboarding);
	const { chats } = useSelector((state: RootState) => state.chat);
	const navigate = useNavigate();
	const dispatch: AppDispatch = useDispatch();
	const { handleLike, handleUpdateMatchesSearch } = useMatch();
	const handleDeleteAction = useHandleDeleteNotification();
	const [isOpenWarning, setIsOpenWarning] = useState(false);
	const [rippleLike, setRippleLike] = useState(false);
	const [rippleDislike, setRippleDislike] = useState(false);

	const handleLikeNotification = async (type: "like" | "dislike") => {
		if (!userInfoProfile || !userInfoProfile.user_id || !userInfoProfile.user_id) return;
		const setRipple = type === "like" ? setRippleLike : setRippleDislike;
		handleLike(userInfoProfile.user_id, type);
		setRipple(true);
		await new Promise(resolve => setTimeout(resolve, 300));
		setRipple(false);
		dispatch(deleteMyNotificationAction({ type: "like", user_id: userInfoProfile.user_id }));
		handleUpdateMatchesSearch(userInfoProfile.user_id);
		handleProfilePreview();
	};

	const handleChatClick = (chatData: Chats | undefined) => {
		if (!chatData) return;
		const chatFound = chats.find(chat => chat.user_id === chatData.user_id);
		navigate(`/messages/${chatData.user_id}`);
		dispatch(setSelectedChat(chatFound || chatData));
		handleProfilePreview();
		handleNotification();
	};

	const handleConfirmAction = () => {
		activeCategory === "myLikes" ? handleDeleteAction("myLikes", userInfoProfile as Likes) : handleDeleteAction("match", userInfoProfile as Likes);
		setIsOpenWarning(false);
		handleProfilePreview();
	}

	const profileActions = (
		<>
			{activeCategory === "myLikes" ? (
				<Column className="gap-2 px-3 pb-3">
					{userInfoProfile?.message_text ? (
						<>
							<div className="w-full border-t-[0.5px] pb-2 border-grayLight" />
							<TextComponent
								variant="h6"
								className="font-bold"
							>
								This <span className="text-primary">Instant Message</span> was sent by you.
							</TextComponent>
              <InstantMessageReceipt viewerId={user?.id || ""} otherId={userInfoProfile.user_id || ""} message={userInfoProfile.message_text} sentByMe />
						</>
					) : (
						<PurchaseMessage
							username={userInfoProfile?.username || ""}
							user_id={userInfoProfile?.user_id || ""}
							notification={{ setUserInfoProfile, userInfoProfile, activeCategory }}
						/>
					)}
					<CustomButton color="white" onClick={() => { setIsOpenWarning(true) }}>
						Remove My Like
					</CustomButton>
				</Column>
			) : (
				<>
					{activeCategory === "like" ? (
						<>
							{userInfoProfile?.message_text && (
								<Column className="gap-2 px-3 pb-3">
									<div className="w-full border-t-[0.5px] border-grayLight" />
									<TextComponent
										variant="h6"
										className="font-bold"
									>
									This <span className="text-primary">Instant Message</span> was sent to you.
									</TextComponent>
                  <InstantMessageReceipt viewerId={user?.id || ""} otherId={userInfoProfile.user_id || ""} message={userInfoProfile.message_text} sentByMe={false} />
								</Column>
							)}
							<Row
								mainAxisAlignment="around"
								crossAxisAlignment="center"
								className={desktopFullPage ? "w-full px-3 pb-3" : "sticky bottom-4 w-full"}
							>
								<ButtonLikeDislike handleLikeMatch={handleLikeNotification} rippleLike={rippleLike} rippleDislike={rippleDislike} />
							</Row>
						</>
					) : (
						<>
							{activeCategory === "match" && (
								<Column className="gap-2 px-3 pb-3">
									<CustomButton color="primary" onClick={() => {
										if (!userInfoProfile?.user_id) return;
										const chatData = {
											chat_id: userInfoProfile.match_id || "",
											user_id: userInfoProfile.user_id || "",
											username: userInfoProfile.username || "",
											profile_pictures: userInfoProfile.profile_pictures?.map(picture => picture.url) || [],
											message_sender_id: "",
											message_id: "",
											message_text: "",
											sent_at: "",
											read: false,
											show_last_logged: userInfoProfile.show_last_logged || false,
											last_logged_in_time: userInfoProfile.last_logged_in_time || "",
											unread_messages: 0,
											type: "text",
											status: "active",
											status_user_id1: "active",
											status_user_id2: "active",
										};
										handleChatClick(chatData);
									}}>
										Send a new message
									</CustomButton>
									<CustomButton color="white" onClick={() => { setIsOpenWarning(true) }}>
										Remove Match
									</CustomButton>
								</Column>
							)}
						</>
					)}
				</>
			)}
		</>
	);

	const profileContent = desktopFullPage ? (
		<Column className="gap-4">
			<Row className="gap-2" crossAxisAlignment="center">
				<button
					type="button"
					onClick={handleProfilePreview}
					className="w-10 aspect-square flex items-center justify-center text-primary transform rotate-90 rounded-full hover:bg-grayDefault"
					aria-label="Back to notifications"
				>
					<ArrowDown2 />
				</button>
				<TextComponent variant="h6" className="font-semibold text-primary">
					Notifications
				</TextComponent>
			</Row>
			<UserView
				pictures={userInfoProfile?.profile_pictures && sortPhotos(userInfoProfile.profile_pictures)}
				name={userInfoProfile?.username}
				verify={userInfoProfile?.verify_status === "verified" ? <CheckVerify /> : <CheckVerify color="#c5c5c5" />}
				resume={[
					userInfoProfile?.age,
					userInfoProfile?.gender,
					userInfoProfile?.sexual_orientation,
					userInfoProfile?.marital_status,
					formatPublicProfileDistanceText(userInfoProfile?.distance, {
						country: user?.country,
						city: user?.city,
					}),
				].filter(Boolean).join(", ")}
				location={userInfoProfile?.city}
				aboutMe={userInfoProfile?.bio}
				desires={userInfoProfile?.desires}
				interest={userInfoProfile?.interests}
				last_logged_in_time={userInfoProfile?.last_logged_in_time}
				show_last_logged={userInfoProfile?.show_last_logged}
				country={userInfoProfile?.country || ""}
				partner={userInfoProfile?.partner}
				isBlur={activeCategory !== "match"}
				full
				className="mx-auto h-auto w-full md:w-full md:max-w-5xl overflow-visible md:overflow-visible rounded-2xl border border-gray-200 shadow-sm"
			>
				{profileActions}
			</UserView>
		</Column>
	) : (
		ReactDOM.createPortal(
			<div className="fixed top-0 left-0 z-30 h-screen w-full bg-[#0000006b]">
				<ProfilePreview
					pictures={userInfoProfile?.profile_pictures}
					username={userInfoProfile?.username}
					verify={userInfoProfile?.verify_status}
					bio={userInfoProfile?.bio}
					desires={userInfoProfile?.desires}
					interest={userInfoProfile?.interests}
					birth={userInfoProfile?.age}
					sexual_orientation={userInfoProfile?.sexual_orientation}
					marital_status={userInfoProfile?.marital_status}
					gender={userInfoProfile?.gender}
					location={userInfoProfile?.city}
					last_logged_in_time={userInfoProfile?.last_logged_in_time}
					show_last_logged={userInfoProfile?.show_last_logged}
					distance={formatPublicProfileDistanceText(userInfoProfile?.distance, {
						country: user?.country,
						city: user?.city,
					})}
					handleProfilePreview={handleProfilePreview}
					country={userInfoProfile?.country || ""}
					partner={userInfoProfile?.partner}
					isBlur={activeCategory !== "match"}
				>
					{profileActions}
				</ProfilePreview>
			</div>,
			document.getElementById("root") as HTMLElement
		)
	);

	return (
		<>
			{profileContent}
			<ConfirmationModal
				isOpen={isOpenWarning}
				category={activeCategory}
				notification={userInfoProfile as Likes}
				onClose={() => setIsOpenWarning(false)}
				onConfirm={() => handleConfirmAction()}
				isLikedSwiped={false}
			/>
		</>
	);
};
